// Vignette d'un fichier déposé sur la page publique : le client VOIT ce qu'il a joint, pas seulement son nom.
//  - image (jpg, jpeg, png) : miniature réelle chargée depuis le backend (GET /formulaires-publics/:jeton/fichiers/:nom,
//    servi en res.sendFile) ; si le navigateur ne peut pas la décoder (HEIC hors Safari, fichier corrompu), repli sur
//    l'icône typée ;
//  - pdf : icône PDF rouge ; doc / docx : icône document bleue ; autre : icône générique ; l'extension est affichée
//    en badge sous l'icône.
// La vignette entière est un lien qui ouvre le fichier dans un nouvel onglet (rel=noopener). Composant pur : aucun
// appel réseau autre que le chargement de l'image par le navigateur, aucun état partagé.
import React, { useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import type { FichierReponse } from './types';
import { tailleLisible } from './libelles';
import { urlFichier } from './formulairesPublicsServices';
import { RAYON, VERT_FONCE } from '../onboarding/tokens';

export interface VignetteFichierProps {
  jeton: string;
  fichier: FichierReponse;
  taille?: number;                 // côté du carré de la miniature en px (défaut 72)
  secondaire?: React.ReactNode;    // texte sous le nom (ex. « Retrait en cours… »)
}

// Extensions que tous les navigateurs savent afficher dans une balise <img>
const EXTENSIONS_IMAGE = ['jpg', 'jpeg', 'png'];

// Extension en minuscules sans le point ('' si aucune) ; le nom réel sur disque (form-<question>-<ts>-<rand>.<ext>)
// est privilégié, le nom d'origine sert de repli
export const extensionFichier = (f: FichierReponse): string => {
  const nom = f.nom || f.nom_original || '';
  const i = nom.lastIndexOf('.');
  return i > 0 ? nom.slice(i + 1).toLowerCase() : '';
};

const IconeType: React.FC<{ ext: string; taille: number }> = ({ ext, taille }) => {
  const sx = { fontSize: Math.round(taille * 0.5) };
  if (ext === 'pdf') return <PictureAsPdfOutlinedIcon sx={{ ...sx, color: '#c62828' }} />;
  if (ext === 'doc' || ext === 'docx') return <DescriptionOutlinedIcon sx={{ ...sx, color: '#1565c0' }} />;
  if (ext === 'heic' || EXTENSIONS_IMAGE.indexOf(ext) >= 0) return <ImageOutlinedIcon sx={{ ...sx, color: '#6a1b9a' }} />;
  return <InsertDriveFileOutlinedIcon sx={{ ...sx, color: 'text.secondary' }} />;
};

const VignetteFichier: React.FC<VignetteFichierProps> = ({ jeton, fichier: f, taille = 72, secondaire }) => {
  const ext = extensionFichier(f);
  const url = urlFichier(jeton, f.nom);
  const [imageKo, setImageKo] = useState(false);
  const afficherImage = EXTENSIONS_IMAGE.indexOf(ext) >= 0 && !imageKo;
  const nom = f.nom_original || f.nom;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, width: '100%' }}>
      <Link
        href={url}
        target="_blank"
        rel="noopener"
        aria-label={`Ouvrir ${nom}`}
        sx={{
          flexShrink: 0,
          width: taille,
          height: taille,
          borderRadius: RAYON,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          textDecoration: 'none',
          '&:hover': { borderColor: VERT_FONCE },
        }}
      >
        {afficherImage ? (
          <Box
            component="img"
            src={url}
            alt={`Aperçu de ${nom}`}
            loading="lazy"
            onError={() => setImageKo(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <>
            <IconeType ext={ext} taille={taille} />
            {ext && (
              <Typography component="span" variant="caption" sx={{ color: 'text.secondary', lineHeight: 1, mt: 0.25, fontSize: 10 }}>
                {ext.toUpperCase()}
              </Typography>
            )}
          </>
        )}
      </Link>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Link
          href={url}
          target="_blank"
          rel="noopener"
          underline="hover"
          variant="body2"
          sx={{ color: VERT_FONCE, fontWeight: 500, wordBreak: 'break-all', display: 'block' }}
        >
          {nom}
        </Link>
        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {secondaire || tailleLisible(f.taille)}
        </Typography>
      </Box>
    </Box>
  );
};

export default VignetteFichier;
